/**
 * frontend/src/tabs/RexTab.jsx
 * SoundDAO Sprint 6A — Full-screen Rex experience (Player home tab)
 *
 * Layout (top → bottom, all breakpoints):
 *   1. Agent header (rendered by inline <Chatbot>)
 *   2. Stats strip — SOUND balance, creators recruited, day streak
 *   3. Inline Rex chat — fills remaining space, no modal/FAB
 *
 * The content is constrained to a centered max-width: 680px reading column
 * via .rex-tab-container so the chat never stretches uncomfortably wide on
 * large monitors.  There is no desktop two-column layout — Rex renders the
 * same structure at every breakpoint.
 *
 * Props (passed from DashboardWrapper / route wrapper):
 *   user    — users row
 *   session — Supabase auth session
 *   balance — current SOUND balance
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import Chatbot from '../Chatbot';
import './RexTab.css';
import { authFetch } from '../lib/authFetch';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

export default function RexTab({ user, session, balance: initialBalance }) {
  const chatRef    = useRef(null);
  // Synchronous guard so the open-with-message effect below runs exactly
  // once per mount, even under React StrictMode's double effect-fire.
  const hasGreeted = useRef(false);
  const user_id    = session?.user?.id || user?.id;

  const [balance,       setBalance]       = useState(initialBalance || 0);
  const [recruitCount,  setRecruitCount]  = useState(0);
  const [streakCount,   setStreakCount]   = useState(0);

  // ── Load stats ──────────────────────────────────────────────────────────────
  const loadStats = useCallback(async () => {
    if (!user_id) return;
    try {
      const balRes = await authFetch(`${API_BASE}/api/rewards/balance/${user_id}`);
      if (balRes.ok) {
        const d = await balRes.json();
        setBalance(d.sound_balance || 0);
      }
    } catch { /* non-fatal */ }

    try {
      const { data: streakRow } = await supabase
        .from('users')
        .select('streak_count')
        .eq('id', user_id)
        .maybeSingle();
      if (streakRow) {
        setStreakCount(streakRow.streak_count || 0);
      }
    } catch { /* non-fatal */ }

    try {
      const { count } = await supabase
        .from('rewards')
        .select('metadata', { count: 'exact', head: true })
        .eq('user_id', user_id)
        .eq('action_type', 'recruit_creator');
      setRecruitCount(count || 0);
    } catch { /* non-fatal */ }

  }, [user_id]);

  useEffect(() => { loadStats(); }, [loadStats]);

  // ── Pick up pending chat message from onboarding ─────────────────────────────
  // The inner Chatbot already fires its own welcome on first mount (see
  // Chatbot.jsx init effect).  We only open-with-message here when there's
  // a pending message from a previous screen (e.g. an onboarding handoff).
  // Ref-guarded so StrictMode's double fire never sends the message twice.
  useEffect(() => {
    if (hasGreeted.current) return;
    hasGreeted.current = true;
    const timer = setTimeout(() => {
      const pendingMsg = localStorage.getItem('sounddao_pending_chat_message');
      if (pendingMsg && chatRef.current) {
        localStorage.removeItem('sounddao_pending_chat_message');
        chatRef.current.openWithMessage(pendingMsg);
      }
      localStorage.removeItem('sounddao_recruited_artist');
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="rex-tab">
      {/* Centered reading column — 680px max on desktop, full-width on mobile */}
      <div className="rex-tab-container">
        {/* Stats strip */}
        <div className="rex-stats-strip">
          <div className="rex-stat-pill">
            <span className="rex-stat-value">{balance.toLocaleString()}</span>
            <span className="rex-stat-label">SOUND</span>
          </div>
          <div className="rex-stat-pill">
            <span className="rex-stat-value">{recruitCount}</span>
            <span className="rex-stat-label">Artists</span>
          </div>
          <div className="rex-stat-pill">
            <span className="rex-stat-value">{streakCount}d</span>
            <span className="rex-stat-label">Streak</span>
          </div>
        </div>

        {/* Full inline chat — fills remaining space.
            <Chatbot inline> renders its own AgentTabHeader at the top. */}
        <Chatbot ref={chatRef} user={user} session={session} inline={true} />
      </div>
    </div>
  );
}
